/**
 * @file Component to view the number of calls
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlertsService } from '../../core/alerts.service';
import { EventsService } from '../../core/event.service';
import { LocalisationService } from '../../core/localisation.service';
import { ForecastService } from '../forecast.service';
import { PluginViewForecastBase, CALLS, OPENING_HOURS, SPECIAL_DAYS, HOLIDAYS } from '../plugin-view-forecast/plugin-view-forecast.base';
import { SettingsService } from '../../settings.service';
import { GridService } from '../../grids/grid.service';
/**
 * Class to show the number of calls.
 */
var PluginViewCallsComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewCallsComponent, _super);
    /**
     * Creates an instance of PluginCallsComponent.
     * @param {LocalisationService} localisationService The localisation service.
     * @param {ChangeDetectorRef} changeDetector The change detector is used to update the rendered layout.
     * @param {EventsService} eventsService The events service.
     * @param {SettingsService} settingsService The settings service.
     * @param {ForecastService} forecastService The forecast service is used to retrieve prognoses.
     * @param {AlertsService} alertsService The alerts service.
     * @memberOf PluginCallsComponent
     */
    function PluginViewCallsComponent(localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService, translateService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) || this;
        _this.translateService = translateService;
        /**
         * The names of the queries that are loaded when the plugin view refreshes.
         * @protected
         * @memberOf PluginViewCallsComponent
         */
        _this.queryNames = [CALLS, OPENING_HOURS, SPECIAL_DAYS, HOLIDAYS];
        _this.subscriptionPool.add(forecastService.processCompleted.subscribe(function () {
            _this.refresh();
        }));
        return _this;
    }
    /**
     * Builds the calls chart.
     * @protected
     * @memberOf PluginCallsComponent
     */
    PluginViewCallsComponent.prototype.build = function () {
        var _this = this;
        var maxValue = 0;
        var pluginBars = [];
        this.gridCollections.forEach(function (gridSegment) {
            gridSegment.children.forEach(function (gridRows) {
                gridRows.children.forEach(function (gridRow) {
                    gridRow.children.forEach(function (gridColumn) {
                        var specialDay = _this.queryResults[SPECIAL_DAYS].find(function (s) { return gridColumn.meta.start.startOf('day').isSame(s['Date']); });
                        if (specialDay) {
                            gridColumn.specialDay = specialDay['Name'];
                        }
                        var holiday = _this.queryResults[HOLIDAYS].find(function (h) { return gridColumn.meta.start.isBetween(h['Start'], h['Finish'], 'day', '[]'); });
                        if (holiday) {
                            gridColumn.specialDay = holiday['Item']['Name'];
                        }
                        gridColumn.pluginBars = [];
                        if (_this.queryResults[CALLS].length === 0) {
                            return;
                        }
                        var dimensions = _this.getBarDimensions(gridColumn);
                        do {
                            var prognoses = _this.queryResults[CALLS].filter(function (p) { return dimensions.startTime.isSameOrBefore(p['DateTime']) && dimensions.endTime.isAfter(p['DateTime']); });
                            if (prognoses.length > 0) {
                                var outside = _this.queryResults[OPENING_HOURS].every(function (p) { return dimensions.startTime.isSameOrAfter(p['Finish']) || dimensions.endTime.isSameOrBefore(p['Start']); });
                                var value = prognoses.length;
                                if (value > maxValue) {
                                    maxValue = value;
                                }
                                var tooltip = value.toString();
                                if (outside) {
                                    tooltip += " (" + _this.translateService.instant('APP.FORECAST.OUTSIDE_OPENING_HOURS') + ")";
                                }
                                var pluginBar = {
                                    startHour: dimensions.startTime.hour(),
                                    startMinute: dimensions.startTime.minute(),
                                    endHour: dimensions.width.asDays() >= 1 ? 24 : dimensions.endTime.hour(),
                                    endMinute: dimensions.width.asDays() >= 1 ? 0 : dimensions.endTime.minute(),
                                    relativeValue: value,
                                    value: tooltip,
                                    class: outside ? 'sym-bar-outside' : ''
                                };
                                gridColumn.pluginBars.push(pluginBar);
                                pluginBars.push(pluginBar);
                            }
                            dimensions.startTime = dimensions.endTime;
                            dimensions.endTime = dimensions.startTime.clone().add(dimensions.width);
                        } while (dimensions.endTime.isSameOrBefore(gridColumn.meta.finish));
                    });
                });
            });
        });
        pluginBars.forEach(function (pluginBar) {
            pluginBar.relativeValue = Math.round(100 * pluginBar.relativeValue / maxValue);
        });
    };
    return PluginViewCallsComponent;
}(PluginViewForecastBase));
export { PluginViewCallsComponent };
